import type { CSSProperties } from 'react';
import styles from './LoadingSkeleton.module.css';

export type SkeletonVariant = 'text' | 'card' | 'badge' | 'gridRow' | 'circle' | 'custom';

export interface LoadingSkeletonProps {
  variant?: SkeletonVariant;
  // For text variant, render N stacked lines. Otherwise ignored.
  lines?: number;
  // Dynamic width/height — counts as a "dynamic value" per spec §0.11 rule #1.
  width?: number | string;
  height?: number | string;
  className?: string;
  ariaLabel?: string;
}

const VARIANT_CLASS: Record<SkeletonVariant, string> = {
  text: styles.text ?? '',
  card: styles.card ?? '',
  badge: styles.badge ?? '',
  gridRow: styles.gridRow ?? '',
  circle: styles.circle ?? '',
  custom: '',
};

export function LoadingSkeleton({
  variant = 'text',
  lines = 1,
  width,
  height,
  className,
  ariaLabel = 'Loading',
}: LoadingSkeletonProps) {
  const baseStyle: CSSProperties = {};
  if (width !== undefined) baseStyle.width = typeof width === 'number' ? `${width}px` : width;
  if (height !== undefined) baseStyle.height = typeof height === 'number' ? `${height}px` : height;

  if (variant === 'text' && lines > 1) {
    return (
      <div className={styles.stack} role="status" aria-label={ariaLabel} aria-busy="true">
        {Array.from({ length: lines }).map((_, i) => {
          // Last line slightly shorter for natural typography.
          const w = i === lines - 1 ? '60%' : '100%';
          return (
            <span
              key={i}
              className={`${styles.skeleton} ${VARIANT_CLASS[variant]} ${className ?? ''}`}
              style={{ width: w, ...baseStyle }}
            />
          );
        })}
      </div>
    );
  }

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      aria-busy="true"
      className={`${styles.skeleton} ${VARIANT_CLASS[variant]} ${className ?? ''}`}
      style={baseStyle}
    />
  );
}
